// Invoice Templates data layer (Phase 4, P4-WP8) — many per company, one default.
//
// The SINGLE interface between the app and public.invoice_templates
// (supabase/migrations/20260701195725_business_settings.sql).
//
// SECURITY MODEL (server-enforced; mirrored here):
//   • company_id forced by set_company_from_auth + immutable; NEVER sent. RLS scopes all.
//   • At most one default per company (partial unique index). is_default is managed
//     ONLY through setDefaultTemplate (unset others → set one), never written directly,
//     so the invariant can't be violated.

import { supabase } from "./supabaseClient";
import { C } from "../customers/theme";

const T = "invoice_templates";

export const LAYOUT_STYLES = [
  { value: "classic", label: "Classic" },
  { value: "modern", label: "Modern" },
  { value: "minimal", label: "Minimal" },
];

/** Shape used to pre-fill a brand-new template. */
export const NEW_TEMPLATE = {
  name: "New template", layout_style: "classic", accent_color: C.primary,
  show_logo: true, show_bank_details: true, show_terms: true, footer_text: "",
};

const WRITE_FIELDS = ["name", "layout_style", "accent_color", "show_logo", "show_bank_details", "show_terms", "footer_text"];

function client() {
  if (!supabase) throw new Error("Supabase is not configured (check .env).");
  return supabase;
}
const pick = (obj, fields) => {
  const out = {};
  for (const f of fields) if (obj && obj[f] !== undefined) out[f] = obj[f] === "" ? null : obj[f];
  return out;
};
function toError(error, fallback) {
  const code = error && error.code;
  let message;
  switch (code) {
    case "23514": message = "Invalid value — check the layout style."; break;
    case "23505": message = "Only one template can be the default."; break;
    case "23502": message = "A required field is missing."; break;
    case "PGRST116": message = "Template not found, or you do not have access to it."; break;
    default: message = (error && error.message) || fallback;
  }
  const e = new Error(message); e.code = code; e.cause = error;
  return e;
}

// ── Reads ────────────────────────────────────────────────────────────────────

/** All templates for the company, default first then newest. @returns {Promise<{data:Array}>} */
export async function getTemplates() {
  const { data, error } = await client().from(T).select("*").order("is_default", { ascending: false }).order("created_at", { ascending: false });
  if (error) throw toError(error, "Could not load templates");
  return { data: data || [] };
}

export async function getTemplate(id) {
  const { data, error } = await client().from(T).select("*").eq("id", id).maybeSingle();
  if (error) throw toError(error, "Could not load template");
  return data || null;
}

/** The company's default template, or null. */
export async function getDefaultTemplate() {
  const { data, error } = await client().from(T).select("*").eq("is_default", true).limit(1).maybeSingle();
  if (error) throw toError(error, "Could not load default template");
  return data || null;
}

// ── Writes ───────────────────────────────────────────────────────────────────

/**
 * Create a template. It becomes the default automatically if it's the company's first,
 * or if `makeDefault` is requested.
 * @param {Object} payload @param {boolean} [makeDefault]
 * @returns {Promise<string>} new template id.
 */
export async function createTemplate(payload, makeDefault = false) {
  const c = client();
  const { data, error } = await c.from(T).insert(pick(payload || {}, WRITE_FIELDS)).select("id").single();
  if (error) throw toError(error, "Could not create template");
  const { data: all } = await getTemplates();
  if (makeDefault || all.length === 1) await setDefaultTemplate(data.id); // first template is always the default
  return data.id;
}

/** Update a template's presentation fields (is_default is managed via setDefaultTemplate). */
export async function updateTemplate(id, payload) {
  const { data, error } = await client().from(T).update(pick(payload || {}, WRITE_FIELDS)).eq("id", id).select("id").single();
  if (error) throw toError(error, "Could not update template");
  return data.id;
}

/**
 * Make a template the company's default: clear any existing default, then set this one.
 * Order matters — the partial unique index forbids two defaults at once.
 * @param {string} id @returns {Promise<void>}
 */
export async function setDefaultTemplate(id) {
  const c = client();
  const { error: clrErr } = await c.from(T).update({ is_default: false }).eq("is_default", true); // RLS scopes to company
  if (clrErr) throw toError(clrErr, "Could not update default template");
  const { error } = await c.from(T).update({ is_default: true }).eq("id", id);
  if (error) throw toError(error, "Could not set default template");
}

/** Delete a template; if it was the default, promote another so a default always exists. */
export async function deleteTemplate(id) {
  const c = client();
  const { error } = await c.from(T).delete().eq("id", id);
  if (error) throw toError(error, "Could not delete template");
  const { data: remaining } = await getTemplates();
  if (remaining.length > 0 && !remaining.some((t) => t.is_default)) await setDefaultTemplate(remaining[0].id);
}
